import { useState } from 'react';

import { Group, Paper, SegmentedControl, Stack, Table, Text } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';

import {
  COST_CATEGORIES,
  COST_CATEGORY_LABELS,
  type CostCategory,
  type MonthEconomics,
} from '@/types/economics';
import { CATEGORY_COLORS, compactNumber, formatAmount, niceTicks } from './palette';
import classes from './Chart.module.css';

const HEIGHT = 240;
const MARGIN = { top: 20, right: 8, bottom: 28, left: 48 };
const MAX_COLUMN = 24;
const GAP = 2;
const RADIUS = 4;
/** Drawn at this width until the container has been measured, so the first
 * frame is a chart rather than every column stacked at the left edge. */
const UNMEASURED_WIDTH = 600;

export interface CostByCategoryChartProps {
  months: MonthEconomics[];
  currency: string;
}

function monthLabel(month: string): string {
  const [year, m] = month.split('-').map(Number);
  return new Date(Date.UTC(year, m - 1, 1)).toLocaleString('en-GB', {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  });
}

/** A column segment with only its top corners rounded. */
function roundedTop(x: number, y: number, w: number, h: number): string {
  const r = Math.min(RADIUS, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

/**
 * Cost per month, split by category: a stacked column per month, categories
 * in their fixed order from the baseline up. Labelled sparingly — the highest
 * month and the latest — with every value in the hover and in the table view.
 */
export function CostByCategoryChart({ months, currency }: Readonly<CostByCategoryChartProps>) {
  const { ref, width: measured } = useElementSize();
  const width = measured > 0 ? measured : UNMEASURED_WIDTH;
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [active, setActive] = useState<number | null>(null);

  const present = COST_CATEGORIES.filter((category) =>
    months.some((m) => (m.totals.costByCategory[category] ?? 0) > 0)
  );
  const max = Math.max(0, ...months.map((m) => m.totals.cost));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1] || 1;
  const plotW = Math.max(width - MARGIN.left - MARGIN.right, 0);
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom;
  const band = months.length ? plotW / months.length : 0;
  const colW = Math.min(MAX_COLUMN, band * 0.6);
  const y = (value: number) => MARGIN.top + plotH - (value / top) * plotH;
  const highest = months.reduce(
    (best, m, i) => (m.totals.cost > (months[best]?.totals.cost ?? -1) ? i : best),
    0
  );
  const money = (value: number) => formatAmount(value, currency);

  const legend = (
    <Group gap="md">
      {present.map((category) => (
        <Group key={category} gap={6} wrap="nowrap">
          <span className={classes.swatch} style={{ backgroundColor: CATEGORY_COLORS[category] }} />
          <Text size="xs">{COST_CATEGORY_LABELS[category]}</Text>
        </Group>
      ))}
    </Group>
  );

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start">
        {legend}
        <SegmentedControl
          size="xs"
          value={view}
          onChange={(v) => setView(v as 'chart' | 'table')}
          data={[
            { value: 'chart', label: 'Chart' },
            { value: 'table', label: 'Table' },
          ]}
        />
      </Group>

      {view === 'table' ? (
        <Table data-testid="cost-by-category-table">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Month</Table.Th>
              {present.map((category) => (
                <Table.Th key={category} ta="right">
                  {COST_CATEGORY_LABELS[category]}
                </Table.Th>
              ))}
              <Table.Th ta="right">Total</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {months.map((m) => (
              <Table.Tr key={m.month}>
                <Table.Td>{monthLabel(m.month)}</Table.Td>
                {present.map((category) => (
                  <Table.Td
                    key={category}
                    ta="right"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {money(m.totals.costByCategory[category] ?? 0)}
                  </Table.Td>
                ))}
                <Table.Td ta="right" fw={500} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {money(m.totals.cost)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : (
        <div ref={ref} style={{ position: 'relative' }}>
          <svg
            className={classes.svg}
            height={HEIGHT}
            role="img"
            aria-label={`Cost per month by category, in ${currency}. Switch to the table for every value.`}
          >
            {ticks.map((tick) => (
              <g key={tick}>
                <line
                  className={tick === 0 ? classes.axis : classes.grid}
                  x1={MARGIN.left}
                  x2={MARGIN.left + plotW}
                  y1={y(tick)}
                  y2={y(tick)}
                />
                <text
                  className={classes.tick}
                  x={MARGIN.left - 8}
                  y={y(tick)}
                  textAnchor="end"
                  dominantBaseline="middle"
                >
                  {compactNumber(tick)}
                </text>
              </g>
            ))}
            {months.map((m, i) => {
              const x = MARGIN.left + band * i + (band - colW) / 2;
              const stacked = present.filter((c) => (m.totals.costByCategory[c] ?? 0) > 0);
              let base = MARGIN.top + plotH;
              const segments = stacked.map((category, index) => {
                const value = m.totals.costByCategory[category] ?? 0;
                const full = (value / top) * plotH;
                // The surface gap comes out of each segment above the first.
                const h = Math.max(full - (index > 0 ? GAP : 0), 0.5);
                const yTop = base - full;
                base = yTop;
                const isTop = index === stacked.length - 1;
                return isTop ? (
                  <path
                    key={category}
                    d={roundedTop(x, yTop, colW, h)}
                    fill={CATEGORY_COLORS[category]}
                  />
                ) : (
                  <rect
                    key={category}
                    x={x}
                    y={yTop}
                    width={colW}
                    height={h}
                    fill={CATEGORY_COLORS[category]}
                  />
                );
              });
              const labelled = (i === highest || i === months.length - 1) && m.totals.cost > 0;
              return (
                <g key={m.month}>
                  <rect
                    className={classes.hit}
                    x={MARGIN.left + band * i}
                    y={MARGIN.top}
                    width={band}
                    height={plotH}
                    tabIndex={0}
                    aria-label={`${monthLabel(m.month)}: ${money(m.totals.cost)}`}
                    onMouseEnter={() => setActive(i)}
                    onMouseLeave={() => setActive(null)}
                    onFocus={() => setActive(i)}
                    onBlur={() => setActive(null)}
                  />
                  <g pointerEvents="none">{segments}</g>
                  {labelled ? (
                    <text
                      className={classes.value}
                      x={x + colW / 2}
                      y={y(m.totals.cost) - 6}
                      textAnchor="middle"
                      pointerEvents="none"
                    >
                      {compactNumber(m.totals.cost)}
                    </text>
                  ) : null}
                  <text
                    className={classes.tick}
                    x={x + colW / 2}
                    y={HEIGHT - 8}
                    textAnchor="middle"
                    pointerEvents="none"
                  >
                    {monthLabel(m.month)}
                  </text>
                </g>
              );
            })}
          </svg>
          {active !== null && months[active] ? (
            <Paper
              withBorder
              shadow="sm"
              p="xs"
              role="tooltip"
              style={{
                position: 'absolute',
                top: 0,
                left: Math.min(
                  MARGIN.left + band * active + band / 2 + 12,
                  Math.max(width - 220, 0)
                ),
                width: 208,
                pointerEvents: 'none',
              }}
            >
              <Text size="xs" fw={600} mb={4}>
                {monthLabel(months[active].month)} · {money(months[active].totals.cost)}
              </Text>
              {present
                .filter((c) => (months[active].totals.costByCategory[c] ?? 0) > 0)
                .reverse()
                .map((category: CostCategory) => (
                  <Group key={category} justify="space-between" gap="xs" wrap="nowrap">
                    <Group gap={6} wrap="nowrap">
                      <span
                        className={classes.swatch}
                        style={{ backgroundColor: CATEGORY_COLORS[category] }}
                      />
                      <Text size="xs">{COST_CATEGORY_LABELS[category]}</Text>
                    </Group>
                    <Text size="xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {money(months[active].totals.costByCategory[category] ?? 0)}
                    </Text>
                  </Group>
                ))}
              {months[active].totals.unrated > 0 ? (
                <Text size="xs" c="dimmed" mt={4}>
                  Plus {months[active].totals.unrated} unrated line(s), not counted
                </Text>
              ) : null}
            </Paper>
          ) : null}
        </div>
      )}
    </Stack>
  );
}
